import React, { useMemo, useContext } from "react";
import { useParams } from "react-router-dom";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { IFunctionalLocation } from "./FunctionalLocationsInterfaces";
import { IEventFunctionalLocationSummaryPerMC } from "entities/Mills/MillsEntityInterfaces";
import { IFailureTypes } from "../MachineCenters/MachineCentersInterfaces";
import { IChartBar } from "./FunctionalLocationsInterfaces";
import { Box, Typography } from "@mui/material";
import { useStyles } from "./FunctionalLocations.styles";
import { useTranslation } from "react-i18next";
import NoData from "components/NoData/NoData.view";
import { BarsChart } from "../../BarChart/BarsChart.view";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import { initialMillFilters } from "pages/Corporate/Locations/MillDashboards/SummaryReport/components/SummaryReportFilters/useMillFilters";
import { handleNameTranslation } from "utilities/localesUtilities";

export const FunctionalLocations = () => {
    const { millId } = useParams();
    const { filters } = useContext(SummaryReportDashboardContext);
    const queryFilters = useMemo(
        () => ({
            ...filters,
            startDate: filters.startDate ? filters.startDate : initialMillFilters.startDate,
            endDate: filters.endDate ? filters.endDate : initialMillFilters.endDate,
            displayIn: filters.displayIn ? filters.displayIn : initialMillFilters.displayIn,
        }),
        [filters],
    );
    const { topFiveFunctionalLocationsByMill } = MillsEntity.GetTopFiveFunctionalLocationsByMill(
        +millId!,
        queryFilters,
    );
    const classes = useStyles();
    const { t, i18n } = useTranslation();

    const dataSortedByFailureType: { [key: string]: IFunctionalLocation } = useMemo(() => {
        const statsObject: { [key: string]: IFunctionalLocation } = {};

        topFiveFunctionalLocationsByMill.forEach((stat: IEventFunctionalLocationSummaryPerMC) => {
            const newFailureType = {
                failure_type_name: stat.failure_type.name,
                failure_type_color: stat.failure_type.color_code,
                failure_type_total_minutes: stat.seconds,
                failure_type_total_occurrences: stat.occurrences,
            };
            const functionalLocationName = handleNameTranslation(
                i18n.language,
                stat.functional_location.translation.translation_names,
            );

            if (Object.keys(statsObject).includes(functionalLocationName)) {
                const existingFailureType = statsObject[functionalLocationName].failure_types.find(
                    (failureType) => failureType.failure_type_name === newFailureType.failure_type_name,
                );

                if (existingFailureType) {
                    existingFailureType.failure_type_total_minutes += newFailureType.failure_type_total_minutes;
                    existingFailureType.failure_type_total_occurrences += newFailureType.failure_type_total_occurrences;
                } else {
                    statsObject[functionalLocationName].failure_types.push(newFailureType);
                }
            } else {
                const values: IFunctionalLocation = {
                    functional_location_name: functionalLocationName,
                    functional_location_id: stat.functional_location.functional_location_id,
                    failure_types: [newFailureType],
                };

                statsObject[values.functional_location_name] = values;
            }

            statsObject[functionalLocationName].total_minutes = statsObject[
                functionalLocationName
            ].failure_types.reduce(
                (acc: number, current: IFailureTypes) => acc + current.failure_type_total_minutes,
                0,
            );

            statsObject[functionalLocationName].total_occurrences = statsObject[
                functionalLocationName
            ].failure_types.reduce(
                (acc: number, current: IFailureTypes) => acc + current.failure_type_total_occurrences,
                0,
            );
        });

        Object.values(statsObject).forEach((stat) => {
            stat.total_minutes = Math.round(stat.total_minutes! / 60) < 1 ? 1 : Math.round(stat.total_minutes! / 60);
            stat.failure_types.forEach(
                (functionalLocation) =>
                    (functionalLocation.failure_type_total_minutes =
                        Math.round(functionalLocation.failure_type_total_minutes! / 60) < 1
                            ? 1
                            : Math.round(functionalLocation.failure_type_total_minutes! / 60)),
            );
        });

        return statsObject;
    }, [topFiveFunctionalLocationsByMill, i18n.language]);

    const functionalLocationsByOccurrences: IFunctionalLocation[] = useMemo(
        () =>
            Object.values(dataSortedByFailureType)
                .sort((a, b) => b.total_occurrences! - a.total_occurrences!)
                .slice(0, 5)
                .reverse(),
        [dataSortedByFailureType],
    );

    const functionalLocationsByMinutes: IFunctionalLocation[] = useMemo(
        () =>
            Object.values(dataSortedByFailureType)
                .filter((item) => item.total_minutes && item.total_minutes > 0)
                .sort((a, b) => b.total_minutes! - a.total_minutes!)
                .slice(0, 5)
                .reverse(),
        [dataSortedByFailureType],
    );

    const barsChartData = useMemo(
        () => [
            filters.displayIn === "minutes"
                ? functionalLocationsByMinutes.map((stat) => {
                      const chartBar: IChartBar = {
                          functionalLocation: stat.functional_location_name,
                      };

                      stat.failure_types.forEach((item) => {
                          chartBar[`${item.failure_type_name}Color`] = item.failure_type_color;
                          chartBar[`${item.failure_type_name}total_minutes`] = item.failure_type_total_minutes;
                      });

                      return chartBar;
                  })
                : functionalLocationsByOccurrences.map((stat) => {
                      const chartBar: IChartBar = {
                          functionalLocation: stat.functional_location_name,
                      };

                      stat.failure_types.forEach((item) => {
                          chartBar[`${item.failure_type_name}Color`] = item.failure_type_color;
                          chartBar[`${item.failure_type_name}total_occurrences`] = item.failure_type_total_occurrences;
                      });

                      return chartBar;
                  }),
        ],
        [filters.displayIn, functionalLocationsByMinutes, functionalLocationsByOccurrences],
    );

    return (
        <Box className={classes.container} sx={{ flexDirection: "column" }}>
            <Box className={classes.titleContainer}>
                <Typography className={classes.title} variant="subtitle1">
                    {t("locations.summary_report.top_five_bad_actors.functional_locations.title")}
                </Typography>
            </Box>
            <Box className={classes.graphContainer}>
                <Box className={classes.graphGridItemContainer}>
                    {!barsChartData[0].length ? (
                        <div className={classes.noData}>
                            <NoData />
                        </div>
                    ) : (
                        <BarsChart data={barsChartData[0]} index="functionalLocation" orientation="horizontal" />
                    )}
                </Box>
            </Box>
        </Box>
    );
};
